/**
 * Goal replay: the rolling pose buffer, the playback clock, and the pure
 * helpers the HUD renders.
 *
 * WHY A POSE BUFFER AND NOT AN INPUT LOG. The simulation is bit-deterministic
 * (see the DETERMINISM section of src/sim/world.ts), so re-simulating the last
 * few seconds from a recorded input log would reproduce the goal exactly and
 * cost almost no memory. It was rejected anyway. Re-simulation needs a full
 * world snapshot at the START of the window, not just the inputs — which is
 * `serializeState` of a live world every few seconds, on the render thread —
 * and it re-runs 540 ticks of physics inside the one frame the goal lands on,
 * which is a guaranteed hitch at the exact moment the player is watching. It
 * also makes the replay a hostage to determinism holding: a single float that
 * drifts turns the replay into a DIFFERENT goal, silently, and only sometimes.
 * A ring of poses is 276 KB, costs 72 floats of copying per tick, and cannot be
 * wrong about what happened because it is what happened.
 *
 * FRAME NUMBERING. Frames are addressed by an ABSOLUTE index that counts up
 * forever from the last `clear`. The ring only retains the newest `capacity` of
 * them, so an index is valid while it is in [`oldest`, `newest`) — which makes
 * "the goal is older than the buffer" a comparison rather than a modular-
 * arithmetic puzzle. Only one frame is recorded per LIVE tick; frozen ticks are
 * not recorded, so a window is always seconds of actual play.
 *
 * FRAMES, NOT SECONDS. The buffer's clock is the fixed 120 Hz tick, so a
 * fractional frame index is the only time coordinate playback needs and the
 * conversion to seconds happens in exactly two places (`replayWindow` and
 * `playbackFrame`).
 */

import { Quaternion } from 'three';
import { DT } from '../physics/constants';
import type { CarInput, CarState } from '../physics/car-state';
import { clamp, dot, forward as carForward, length, vec3, type Vec3 } from '../physics/math';
import { orientationToQuaternion } from './convert';

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/** Floats per car per frame. See `record` for the order. */
const CAR_STRIDE = 11;
/** Floats per frame for the ball: position (3) and angular velocity (3). */
const BALL_STRIDE = 6;

const FLAG_BOOSTING = 1;
const FLAG_DEMOLISHED = 2;
const FLAG_SUPERSONIC = 4;

/**
 * How much play the ring holds. Comfortably more than the longest window
 * playback can ask for (`REPLAY_SOURCE_SECONDS`), so the slowest possible
 * schedule still has frames to reach back to.
 */
export const REPLAY_BUFFER_SECONDS = 8;
export const REPLAY_BUFFER_FRAMES = Math.round(REPLAY_BUFFER_SECONDS / DT);

// ---------------------------------------------------------------------------
// Poses
// ---------------------------------------------------------------------------

export interface ReplayCarPose {
  /** PHYSICS frame, exactly like `CarState.position`. */
  position: Vec3;
  /** THREE frame, exactly what `orientationToQuaternion` produces. */
  quaternion: Quaternion;
  boosting: boolean;
  demolished: boolean;
  supersonic: boolean;
  /** Signed speed along the car's own forward axis, for the wheel spin. */
  forwardSpeed: number;
  /** Steering input at the time, for the front wheels. */
  steer: number;
  /** |velocity| in uu/s. */
  speed: number;
}

export interface ReplayPose {
  /** PHYSICS frame. */
  ball: Vec3;
  /** Ball angular velocity, PHYSICS frame, so playback can spin the mesh. */
  ballSpin: Vec3;
  cars: ReplayCarPose[];
}

export const makeReplayPose = (carCount: number): ReplayPose => ({
  ball: vec3(),
  ballSpin: vec3(),
  cars: Array.from({ length: carCount }, () => ({
    position: vec3(),
    quaternion: new Quaternion(),
    boosting: false,
    demolished: false,
    supersonic: false,
    forwardSpeed: 0,
    steer: 0,
    speed: 0,
  })),
});

// Scratch for the slerp; two are needed because `slerpQuaternions` reads both
// endpoints. Module-level so sampling allocates nothing on the render path.
const _qa = new Quaternion();
const _qb = new Quaternion();
const _scratchQuat = new Quaternion();

// ---------------------------------------------------------------------------
// The ring
// ---------------------------------------------------------------------------

export class ReplayBuffer {
  readonly capacity: number;
  readonly carCount: number;

  private readonly data: Float32Array;
  private readonly stride: number;
  /** Total frames recorded since the last `clear`; also the next absolute index. */
  private written = 0;

  constructor(carCount: number, capacity: number = REPLAY_BUFFER_FRAMES) {
    this.carCount = Math.max(0, carCount);
    this.capacity = Math.max(1, capacity);
    this.stride = BALL_STRIDE + this.carCount * CAR_STRIDE;
    this.data = new Float32Array(this.capacity * this.stride);
  }

  /** Frames currently retained. Never more than `capacity`. */
  get length(): number {
    return Math.min(this.written, this.capacity);
  }

  /** Absolute index of the oldest retained frame. */
  get oldest(): number {
    return this.written - this.length;
  }

  /** One past the absolute index of the newest retained frame. */
  get newest(): number {
    return this.written;
  }

  /** Bytes the ring occupies, for the budget report. */
  get bytes(): number {
    return this.data.byteLength;
  }

  /**
   * Forget everything. Called on every kickoff: a replay must never reach back
   * across a stoppage into the previous play, and clamping a short window is a
   * far better answer than showing the wrong goal's build-up.
   */
  clear(): void {
    this.written = 0;
  }

  /**
   * Record one live tick.
   *
   * `inputs` is indexed in parallel with `cars`, exactly as `World.step` takes
   * it; a missing entry records as neutral. Orientation is stored already
   * converted to the Three.js frame because that is the only form the renderer
   * ever wants and converting once per tick beats converting once per sampled
   * frame at 144 Hz.
   */
  record(ball: Vec3, ballSpin: Vec3, cars: readonly CarState[], inputs: readonly CarInput[]): void {
    const base = (this.written % this.capacity) * this.stride;
    const d = this.data;

    d[base] = ball.x;
    d[base + 1] = ball.y;
    d[base + 2] = ball.z;
    d[base + 3] = ballSpin.x;
    d[base + 4] = ballSpin.y;
    d[base + 5] = ballSpin.z;

    for (let i = 0; i < this.carCount; i++) {
      const car = cars[i];
      const o = base + BALL_STRIDE + i * CAR_STRIDE;
      if (!car) {
        for (let k = 0; k < CAR_STRIDE; k++) d[o + k] = 0;
        d[o + 6] = 1; // an identity quaternion, not an all-zero one
        continue;
      }
      const input = inputs[i];
      const q = orientationToQuaternion(car.orientation, _scratchQuat);

      d[o] = car.position.x;
      d[o + 1] = car.position.y;
      d[o + 2] = car.position.z;
      d[o + 3] = q.x;
      d[o + 4] = q.y;
      d[o + 5] = q.z;
      d[o + 6] = q.w;

      const boosting = (input?.boost ?? false) && car.boostAmount > 0 && !car.demolished;
      d[o + 7] =
        (boosting ? FLAG_BOOSTING : 0) |
        (car.demolished ? FLAG_DEMOLISHED : 0) |
        (car.supersonic ? FLAG_SUPERSONIC : 0);
      d[o + 8] = dot(carForward(car.orientation), car.velocity);
      d[o + 9] = input?.steer ?? 0;
      d[o + 10] = length(car.velocity);
    }

    this.written++;
  }

  /** True while `index` names a frame the ring still holds. */
  has(index: number): boolean {
    return index >= this.oldest && index < this.newest;
  }

  /**
   * Sample at a FRACTIONAL absolute frame index, into `out`.
   *
   * Positions and scalars are lerped and orientations slerped, so playback is
   * smooth at any speed and at any refresh rate — which is the entire point of
   * being able to run the clock at 0.25x. Booleans take the nearer frame; there
   * is no meaningful interpolation of "boosting" and blending it would light the
   * flame at half brightness for one frame either side of every press.
   *
   * Out-of-range indices CLAMP to the retained ends rather than wrapping or
   * returning garbage: playback is driven by a float clock and lands a hair past
   * the last frame on the final tick of every single replay.
   */
  sample(index: number, out: ReplayPose): void {
    const lo = this.oldest;
    const hi = this.newest - 1;
    if (hi < lo) return;

    const clamped = clamp(index, lo, hi);
    const i0 = Math.floor(clamped);
    const i1 = i0 + 1 > hi ? hi : i0 + 1;
    const t = clamped - i0;

    const a = (i0 % this.capacity) * this.stride;
    const b = (i1 % this.capacity) * this.stride;
    const d = this.data;

    out.ball.x = d[a] + (d[b] - d[a]) * t;
    out.ball.y = d[a + 1] + (d[b + 1] - d[a + 1]) * t;
    out.ball.z = d[a + 2] + (d[b + 2] - d[a + 2]) * t;
    out.ballSpin.x = d[a + 3] + (d[b + 3] - d[a + 3]) * t;
    out.ballSpin.y = d[a + 4] + (d[b + 4] - d[a + 4]) * t;
    out.ballSpin.z = d[a + 5] + (d[b + 5] - d[a + 5]) * t;

    const near = t < 0.5 ? a : b;
    for (let i = 0; i < this.carCount; i++) {
      const pose = out.cars[i];
      if (!pose) continue;
      const oa = a + BALL_STRIDE + i * CAR_STRIDE;
      const ob = b + BALL_STRIDE + i * CAR_STRIDE;

      pose.position.x = d[oa] + (d[ob] - d[oa]) * t;
      pose.position.y = d[oa + 1] + (d[ob + 1] - d[oa + 1]) * t;
      pose.position.z = d[oa + 2] + (d[ob + 2] - d[oa + 2]) * t;

      _qa.set(d[oa + 3], d[oa + 4], d[oa + 5], d[oa + 6]);
      _qb.set(d[ob + 3], d[ob + 4], d[ob + 5], d[ob + 6]);
      pose.quaternion.slerpQuaternions(_qa, _qb, t);

      const flags = d[near + BALL_STRIDE + i * CAR_STRIDE + 7];
      pose.boosting = (flags & FLAG_BOOSTING) !== 0;
      pose.demolished = (flags & FLAG_DEMOLISHED) !== 0;
      pose.supersonic = (flags & FLAG_SUPERSONIC) !== 0;

      pose.forwardSpeed = d[oa + 8] + (d[ob + 8] - d[oa + 8]) * t;
      pose.steer = d[oa + 9] + (d[ob + 9] - d[oa + 9]) * t;
      pose.speed = d[oa + 10] + (d[ob + 10] - d[oa + 10]) * t;
    }
  }
}

// ---------------------------------------------------------------------------
// The playback clock
// ---------------------------------------------------------------------------

/** One constant-rate stretch of the playback schedule. */
export interface ReplaySegment {
  /** Playback time this segment ends at, in seconds. Strictly increasing. */
  until: number;
  /** Recorded seconds consumed per playback second. 1 is real time. */
  rate: number;
}

/**
 * The schedule, and the reason the replay feels like Rocket League rather than
 * like a video scrub.
 *
 * It ramps DOWN. The build-up runs slightly fast because nobody needs to watch
 * a car cross midfield in real time; the strike drops to half; the ball crossing
 * the line — which is the shot everybody actually wants — plays at a quarter
 * speed. The deep slow-mo is deliberately at the END rather than in the middle
 * because the one moment whose timing is known exactly is the goal itself: the
 * window is anchored to it, and the moment the ball was struck is not.
 */
export const REPLAY_SEGMENTS: readonly ReplaySegment[] = [
  { until: 2.4, rate: 1.3 },
  { until: 4.0, rate: 0.55 },
  { until: 6.0, rate: 0.25 },
];

/** Nominal replay length the schedule is written for, in seconds. */
export const REPLAY_OUTPUT_SECONDS = REPLAY_SEGMENTS[REPLAY_SEGMENTS.length - 1].until;

/**
 * Recorded seconds consumed by the first `t` seconds of playback.
 *
 * Piecewise linear, continuous, and non-decreasing by construction — every rate
 * is positive — so it has an unambiguous inverse and playback can never run
 * backwards. Clamped at both ends.
 */
export const replaySourceOffset = (t: number): number => {
  if (!Number.isFinite(t) || t <= 0) return 0;
  let consumed = 0;
  let from = 0;
  for (const segment of REPLAY_SEGMENTS) {
    const span = segment.until - from;
    if (t < segment.until) return consumed + (t - from) * segment.rate;
    consumed += span * segment.rate;
    from = segment.until;
  }
  return consumed;
};

/** Recorded seconds a full-length replay asks for. ~4.5 s on the schedule above. */
export const REPLAY_SOURCE_SECONDS = replaySourceOffset(REPLAY_OUTPUT_SECONDS);

// ---------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------

export interface ReplayWindow {
  /** Absolute frame index playback starts on. */
  start: number;
  /** Absolute frame index playback ends on — the frame the ball crossed. */
  end: number;
  /** Recorded seconds the window spans. Zero for a single-frame window. */
  duration: number;
}

/**
 * The slice of the ring a replay should play: `lead` seconds ending on
 * `goalFrame`, clamped to what is actually retained.
 *
 * A goal scored two seconds after a kickoff has two seconds of build-up and
 * gets exactly that, because the ring is cleared on every kickoff. The schedule
 * is then rescaled onto the shorter window (see `playbackFrame`) rather than
 * the replay simply starting late — a short replay should be a slower replay,
 * not a replay with dead air at the front.
 */
export const replayWindow = (
  buffer: ReplayBuffer,
  goalFrame: number,
  lead: number = REPLAY_SOURCE_SECONDS
): ReplayWindow => {
  const end = clamp(goalFrame, buffer.oldest, Math.max(buffer.oldest, buffer.newest - 1));
  const wanted = Math.round(Math.max(0, lead) / DT);
  const start = Math.max(buffer.oldest, end - wanted);
  return { start, end, duration: (end - start) * DT };
};

/**
 * Fractional frame index to display at `elapsed` seconds into the replay.
 *
 * `outputDuration` is the match config's `replayDuration`, so a non-default
 * replay length stretches the SAME schedule rather than truncating it — the
 * shape of the slow-mo is a design decision and should not depend on a setting.
 */
export const playbackFrame = (
  window: ReplayWindow,
  elapsed: number,
  outputDuration: number = REPLAY_OUTPUT_SECONDS
): number => {
  const span = window.end - window.start;
  if (span <= 0) return window.end;

  const d = Number.isFinite(outputDuration) && outputDuration > 0
    ? outputDuration
    : REPLAY_OUTPUT_SECONDS;
  const t = clamp(elapsed, 0, d) * (REPLAY_OUTPUT_SECONDS / d);
  // Rescale onto whatever the ring actually held, so the window always finishes
  // exactly on the goal frame however short it is.
  const scale = window.duration / REPLAY_SOURCE_SECONDS;
  return window.start + (replaySourceOffset(t) * scale) / DT;
};

// ---------------------------------------------------------------------------
// Skipping
// ---------------------------------------------------------------------------

/**
 * "Press any key to skip", as an EDGE rather than a level.
 *
 * A player who was flooring the throttle when the ball crossed the line is
 * still flooring it a frame later, and a level-triggered skip would eat their
 * replay before the net had finished moving. The latch therefore starts ARMED
 * SHUT: it needs the input to go quiet once, and fires on the next press. The
 * replay ends on its own timer regardless, so a player who never lets go is
 * never trapped — they just watch it.
 */
export class SkipLatch {
  private down = true;

  /** Re-arm. Called on the tick a goal is scored. */
  reset(): void {
    this.down = true;
  }

  /** Feed this tick's "is anything held", and get back "skip now". */
  update(anyInput: boolean): boolean {
    const fired = anyInput && !this.down;
    this.down = anyInput;
    return fired;
  }
}

/**
 * The other half of the skip: presses caught as EVENTS rather than sampled.
 *
 * `SkipLatch` reads a level once per 120 Hz tick, which is the only shape a
 * gamepad offers and is fine for one. It is not fine for a keyboard. A tap is
 * a keydown and a keyup a few milliseconds apart, and the eight-millisecond
 * gap between two samples is wide enough to swallow one whole — measured in
 * the browser, a quick key press skipped nothing at all perhaps a third of the
 * time. An event cannot be missed, so keyboard and pointer go through here and
 * the pad keeps the latch.
 *
 * `repeat` is ignored, which is what stops a player who is holding the throttle
 * from skipping the replay the moment the key auto-repeats.
 *
 * The target is injected rather than assumed to be `window`, so this module
 * still imports and tests in Node.
 */
export class SkipInput {
  private pressed = false;
  private readonly onKey: (event: Event) => void;
  private readonly onPointer: () => void;

  constructor(private readonly target: EventTarget) {
    this.onKey = (event: Event): void => {
      if ((event as KeyboardEvent).repeat) return;
      this.pressed = true;
    };
    this.onPointer = (): void => {
      this.pressed = true;
    };
    target.addEventListener('keydown', this.onKey);
    target.addEventListener('pointerdown', this.onPointer);
  }

  /** True once per press. Reading it clears the latch. */
  consume(): boolean {
    const pressed = this.pressed;
    this.pressed = false;
    return pressed;
  }

  /** Drop anything caught but not yet read — a goal, or an unpause. */
  clear(): void {
    this.pressed = false;
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onKey);
    this.target.removeEventListener('pointerdown', this.onPointer);
  }
}

// ---------------------------------------------------------------------------
// HUD strings
// ---------------------------------------------------------------------------

/**
 * Who to credit on the replay card.
 *
 * Cars have no names in this tree, so the label is positional: the human's car
 * is "You", everyone else is their team and their number within it. `scorer` is
 * null for an own goal, which the HUD says out loud rather than crediting the
 * team that benefited.
 */
export const scorerLabel = (
  scorer: number | null,
  team: 0 | 1,
  human: number | null,
  teamSize: number
): string => {
  if (scorer === null) return 'Own goal';
  if (human !== null && scorer === human) return 'You';
  const size = Math.max(1, teamSize);
  const indexInTeam = scorer % size;
  return `${team === 0 ? 'Blue' : 'Orange'} ${indexInTeam + 1}`;
};

/** Shot speed for the HUD. Same unit as the goal banner, so they agree. */
export const formatShotSpeed = (speed: number): string =>
  `${Number.isFinite(speed) ? Math.round(Math.max(0, speed)) : 0}`;
